import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  MessageSquare, Send, Inbox, Bell, X, Loader2,
  ChevronDown, ChevronUp, Users, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function CommunicationHub({ user }) {
  const [messages, setMessages] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showCompose, setShowCompose] = useState(false);
  const [activeTab, setActiveTab] = useState('inbox');
  const [teams, setTeams] = useState([]);
  
  const [newMessage, setNewMessage] = useState({
    recipient_id: '',
    team_id: '',
    subject: '',
    content: '',
    priority: 'normal',
    is_broadcast: false
  });

  useEffect(() => {
    fetchMessages();
    fetchUnreadCount();
    fetchTeams();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/communications/messages?folder=${activeTab}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/communications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data.teams || []);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.content.trim()) {
      toast.error('Message content is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/communications/messages`, {
        ...newMessage,
        recipient_id: newMessage.is_broadcast ? null : newMessage.recipient_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message sent successfully');
      setShowCompose(false);
      setNewMessage({ recipient_id: '', team_id: '', subject: '', content: '', priority: 'normal', is_broadcast: false });
      fetchMessages();
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  const markAsRead = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/communications/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMessages();
      fetchUnreadCount();
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6" data-testid="communication-hub">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center">
            <MessageSquare className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Communication Hub</h3>
            <p className="text-xs text-zinc-500">Messages & Announcements</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
              {unreadCount} unread
            </span>
          )}
          <Button
            onClick={() => setShowCompose(true)}
            className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm"
            data-testid="compose-message-btn"
          >
            <Send className="h-4 w-4 mr-1" />
            Compose
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-4">
        {['inbox', 'sent', 'all'].map(tab => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); fetchMessages(); }}
            className={`px-4 py-2 rounded-lg text-sm transition-all ${
              activeTab === tab 
                ? 'bg-blue-500/20 text-blue-400' 
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Messages List */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            <Inbox className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No messages yet</p>
          </div>
        ) : (
          messages.map(msg => (
            <div
              key={msg.id}
              onClick={() => !msg.is_read && markAsRead(msg.id)}
              className={`p-4 rounded-xl cursor-pointer transition-all ${
                msg.is_read 
                  ? 'bg-zinc-800/30 border border-zinc-700/30' 
                  : 'bg-blue-500/10 border border-blue-500/30'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-white text-sm">{msg.subject || 'No Subject'}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {msg.is_broadcast ? '📢 Broadcast' : `From: ${msg.sender_name}`}
                  </p>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  msg.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                  msg.priority === 'high' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-zinc-700 text-zinc-400'
                }`}>
                  {msg.priority}
                </span>
              </div>
              <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{msg.content}</p>
            </div>
          ))
        )}
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCompose(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">New Message</h3>
              <button onClick={() => setShowCompose(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-zinc-400">
                  <input
                    type="checkbox"
                    checked={newMessage.is_broadcast}
                    onChange={e => setNewMessage({...newMessage, is_broadcast: e.target.checked})}
                    className="rounded bg-zinc-800 border-zinc-700"
                  />
                  Broadcast to Team
                </label>
              </div>

              {newMessage.is_broadcast && (
                <select
                  value={newMessage.team_id}
                  onChange={e => setNewMessage({...newMessage, team_id: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white"
                >
                  <option value="">Select Team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              )}

              <Input
                placeholder="Subject"
                value={newMessage.subject}
                onChange={e => setNewMessage({...newMessage, subject: e.target.value})}
                className="bg-zinc-800 border-zinc-700 text-white"
              />

              <textarea
                placeholder="Message content..."
                value={newMessage.content}
                onChange={e => setNewMessage({...newMessage, content: e.target.value})}
                rows={5}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white resize-none"
              />

              <select
                value={newMessage.priority}
                onChange={e => setNewMessage({...newMessage, priority: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white"
              >
                <option value="normal">Normal Priority</option>
                <option value="high">High Priority</option>
                <option value="urgent">Urgent</option>
              </select>

              <Button
                onClick={sendMessage}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Message
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
